import {useRef, useState} from 'react';

import {useTranslation} from 'react-i18next';

import {CloudUpload, Close, Description} from '@mui/icons-material';
import {Box, Typography, IconButton, Chip, useTheme} from '@mui/material';

import {getDragAndDropStyles} from './DragAndDropStyles';

type DocumentFile = File | null;

interface Props {
  value: DocumentFile;
  onChange: (file: DocumentFile) => void;
  error?: string;
  accept?: string;
  maxBytes?: number;
  label?: string;
  supportedFormats?: string[];
}

const DEFAULT_ACCEPT = 'application/pdf';
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024; // 10MB

export const DragAndDropDocumentUpload = ({
  value,
  onChange,
  error,
  accept = DEFAULT_ACCEPT,
  maxBytes = DEFAULT_MAX_BYTES,
  label,
  supportedFormats = ['PDF'],
}: Props) => {
  const {t} = useTranslation();
  const theme = useTheme();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [localError, setLocalError] = useState<string>('');

  const styles = getDragAndDropStyles(theme);

  const validate = (file: File): string => {
    if (file.size > maxBytes) {
      return t('applicantProfile.documents.dragAndDrop.errors.fileTooBig', {
        maxSize: (maxBytes / (1024 * 1024)).toFixed(0),
      });
    }

    const ext = file.name.toLowerCase().split('.').pop() || '';
    const allowedExtensions = ['pdf'];

    if (!allowedExtensions.includes(ext)) {
      return t('applicantProfile.documents.dragAndDrop.errors.invalidFileType', {
        supportedTypes: supportedFormats.join(', '),
      });
    }

    return '';
  };

  const handleFiles = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const file = files[0];
    const validationError = validate(file);
    if (validationError) {
      setLocalError(validationError);
      onChange(null);
    } else {
      setLocalError('');
      onChange(file);
    }
  };

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const onDragLeave = () => {
    setIsDragging(false);
  };

  const openFileDialog = () => inputRef.current?.click();

  const removeFile = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(null);
    setLocalError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const shownError = error || localError;

  const getDropZoneStyles = () => ({
    ...styles.dropZone,
    ...(isDragging && styles.dropZoneActive),
    ...(shownError && styles.dropZoneError),
  });

  return (
    <Box>
      {label && (
        <Typography variant='body2' sx={{mb: 1, fontWeight: 500}}>
          {label}
        </Typography>
      )}

      <Box
        sx={getDropZoneStyles()}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onClick={openFileDialog}
      >
        <input
          ref={inputRef}
          type='file'
          accept={accept}
          onChange={(e) => handleFiles(e.target.files)}
          style={styles.hiddenInput}
        />

        {!value ? (
          <Box>
            <CloudUpload sx={styles.uploadIcon} />
            <Typography variant='body1' sx={{fontWeight: 500, mb: 1}}>
              {isDragging
                ? t('applicantProfile.documents.dragAndDrop.dropFileHere')
                : t('applicantProfile.documents.dragAndDrop.dragFileHere')}
            </Typography>
            <Typography variant='body2' color='text.secondary' sx={{mb: 2}}>
              {t('applicantProfile.documents.dragAndDrop.orClickToSelect')}
            </Typography>
            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: 'center', mb: 1}}>
              {supportedFormats.map((format) => (
                <Chip key={format} label={format} size='small' sx={styles.chip} />
              ))}
            </Box>
            <Typography variant='caption' color='text.secondary'>
              {t('applicantProfile.documents.dragAndDrop.maxFileSize', {
                maxSize: (maxBytes / (1024 * 1024)).toFixed(0),
              })}
            </Typography>
          </Box>
        ) : (
          <Box sx={styles.fileRow}>
            <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0}}>
              <Description />
              <Typography variant='body2' sx={styles.fileName} title={value.name}>
                {value.name}
              </Typography>
            </Box>
            <IconButton size='small' onClick={removeFile} sx={styles.removeButton}>
              <Close fontSize='small' />
            </IconButton>
          </Box>
        )}
      </Box>

      {shownError && <Typography sx={styles.errorText}>{shownError}</Typography>}
    </Box>
  );
};
